/** App-level state: signals over the DB. The DB is the source of truth; signals are a view of it. */
import { signal } from '@preact/signals';
import { getSettings, listLive, listByJob } from '../core/db';
import { onChangeFromOtherTab } from '../core/bus';
import type { Client, InventoryItem, Job, PriceItem, ReviewItem, Settings, Shape, TakeoffLine, DocumentRecord } from '../core/types';

export interface Route { view: 'jobs' | 'job' | 'field' | 'clients' | 'prices' | 'settings' | 'backup'; id?: string; sub?: string }

function parseHash(): Route {
  const h = location.hash.replace(/^#\/?/, '');
  const [view, id, kind, docId] = h.split('/');
  if (view === 'job' && id) return { view: 'job', id, sub: kind === 'doc' ? docId : undefined };
  if (view === 'field' && id) return { view: 'field', id };
  if (view === 'clients' || view === 'prices' || view === 'settings' || view === 'backup') return { view };
  return { view: 'jobs' };
}

export const route = signal<Route>(parseHash());
window.addEventListener('hashchange', () => { route.value = parseHash(); });
export const nav = (h: string): void => { location.hash = h; };

export const jobsSig = signal<Job[]>([]);
export const clientsSig = signal<Client[]>([]);
export const pricesSig = signal<PriceItem[]>([]);
export const settingsSig = signal<Settings | null>(null);
export const toastSig = signal<string | null>(null);
export const onlineSig = signal<boolean>(navigator.onLine);
window.addEventListener('online', () => { onlineSig.value = true; });
window.addEventListener('offline', () => { onlineSig.value = false; });

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string): void {
  toastSig.value = msg;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastSig.value = null; }, 3500);
}

export async function refreshCore(): Promise<void> {
  const [jobs, prices, settings, clients] = await Promise.all([
    listLive('jobs'), listLive('priceBook'), getSettings(), listLive('clients'),
  ]);
  clientsSig.value = clients.sort((a, b) => (a.company || a.name).localeCompare(b.company || b.name));
  jobsSig.value = jobs.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  pricesSig.value = prices;
  settingsSig.value = settings;
}

/**
 * A second tab changed the database, so this tab's lists are behind. Reload
 * them. Without this, a tab left open shows a job list from whenever it booted
 * and saves screens built on that stale picture — which is how two jobs ended
 * up with the same bid number (see core/bus.ts).
 */
onChangeFromOtherTab(() => { void refreshCore(); });

/** everything one job view needs, loaded together */
export interface JobBundle {
  job: Job;
  lines: TakeoffLine[];
  inventory: InventoryItem[];
  shapes: Shape[];
  documents: DocumentRecord[];
  review: ReviewItem[];
}

export const jobBundleSig = signal<JobBundle | null>(null);

export async function loadJobBundle(jobId: string): Promise<void> {
  const jobs = await listLive('jobs');
  const job = jobs.find((j) => j.id === jobId);
  if (!job) { jobBundleSig.value = null; return; }
  const [lines, inventory, shapes, documents, reviewAll] = await Promise.all([
    listByJob('takeoffLines', jobId), listByJob('inventoryItems', jobId),
    listByJob('shapes', jobId), listByJob('documents', jobId),
    listLive('reviewQueue'),
  ]);
  jobBundleSig.value = {
    job,
    lines: lines.sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    inventory: inventory.sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    shapes, documents,
    review: reviewAll.filter((r) => r.jobId === jobId).sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
  };
}
